import { useState } from "react";
import { View, Text } from "react-native";
import { TextActionButton } from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { EMBER } from "../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

export type FactSheetContextRow = {
  label: string;
  value: string;
};

function FactSheetContextInfoRow({ label, value }: FactSheetContextRow) {
  const isLongRow = label.length > 18 || value.length > 32;

  return (
    <View
      style={{
        flexDirection: isLongRow ? "column" : "row",
        alignItems: isLongRow ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: isLongRow ? 3 : 14,
        paddingVertical: 9,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: "700",
          color: EMBER.colors.textLight,
          letterSpacing: 0,
          lineHeight: 16,
          width: isLongRow ? undefined : 110,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 18,
          flex: isLongRow ? undefined : 1,
          textAlign: isLongRow ? "left" : "right",
        }}
        numberOfLines={isLongRow ? 3 : 2}
      >
        {value}
      </Text>
    </View>
  );
}

export default function FactSheetContextCard({
  label,
  meta,
  rows,
  collapsedRowCount = 4,
  expandLabel,
  collapseLabel,
  actionLabel,
  onActionPress,
}: {
  label: string;
  meta?: string | null;
  rows: FactSheetContextRow[];
  collapsedRowCount?: number;
  expandLabel: string;
  collapseLabel: string;
  actionLabel?: string;
  onActionPress?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = rows.length > collapsedRowCount;
  const leadingRows = shouldCollapse ? rows.slice(0, collapsedRowCount) : rows;
  const foldedRows = shouldCollapse ? rows.slice(collapsedRowCount) : [];
  const renderRow = (row: FactSheetContextRow, index: number) => (
    <View
      key={`${row.label}-${index}`}
      style={{
        borderTopWidth: index === 0 ? 0 : 1,
        borderTopColor: semanticColors.border.subtle,
      }}
    >
      <FactSheetContextInfoRow {...row} />
    </View>
  );

  return (
    <View
      style={{
        backgroundColor: semanticColors.surface.subtle,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginBottom: 16,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "800",
            color: EMBER.colors.text,
            lineHeight: 18,
            flex: 1,
          }}
          numberOfLines={2}
        >
          {label}
        </Text>
        {meta ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.textLight,
              lineHeight: 16,
              textAlign: "right",
              flexShrink: 0,
              maxWidth: "46%",
            }}
            numberOfLines={1}
          >
            {meta}
          </Text>
        ) : null}
      </View>

      {rows.length > 0 ? (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
            marginTop: 10,
          }}
        >
          {leadingRows.map((row, index) => renderRow(row, index))}
          {shouldCollapse ? (
            <Accordion
              type="multiple"
              value={expanded ? ["more"] : []}
              onValueChange={(value: string[]) =>
                setExpanded(value.includes("more"))
              }
            >
              <AccordionItem value="more">
                <AccordionContent className="pb-0">
                  {foldedRows.map((row, index) =>
                    renderRow(row, collapsedRowCount + index),
                  )}
                </AccordionContent>
                <AccordionTrigger
                  analyticsLabel={expanded ? collapseLabel : expandLabel}
                  action={{
                    expand: "expand_context",
                    collapse: "collapse_context",
                  }}
                  className="items-center justify-start gap-1 pb-0 pt-2.5"
                  style={{
                    borderTopWidth: 1,
                    borderTopColor: semanticColors.border.subtle,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "800",
                      color: EMBER.colors.textMid,
                      lineHeight: 16,
                    }}
                  >
                    {expanded ? collapseLabel : expandLabel}
                  </Text>
                </AccordionTrigger>
              </AccordionItem>
            </Accordion>
          ) : null}
        </View>
      ) : null}

      {actionLabel && onActionPress ? (
        <TextActionButton
          label={actionLabel}
          action="fact_sheet_context_action"
          size="sm"
          onPress={onActionPress}
          style={{
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
            paddingTop: 10,
            paddingBottom: 0,
            paddingHorizontal: 0,
            marginTop: rows.length > 0 ? 1 : 10,
          }}
        />
      ) : null}
    </View>
  );
}
